import React from 'react';
import PropTypes from 'prop-types';
import { map, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { FiDownload, IoDocumentTextOutline } from 'react-icons/all';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Divider,
  colors,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import UploadDialog from './UploadDialog';
import ConfirmDialog from 'components/ConfirmDialog';
import { MdDeleteForever } from 'react-icons/md';

const useStyles = makeStyles((theme) => ({
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900],
    },
    minWidth: 87,
    marginLeft: '20px!important',
  },
  dialog: {
    width: 460,
    marginBottom: 20,
  },
  actions: {
    margin: '8px 12px',
  },
}));

const Attachment = ({
  data,
  title,
  multiple,
  typeText,
  subtitle,
  acceptFiles,
  isUploading,
  handleUpload,
  handleDelete,
  selectedFiles,
  handleDownload,
  openUploadDialog,
  setSelectedFiles,
  noFileDescription,
  setOpenUploadDialog,
  noDivider,
  deleteDialogTitle,
  deleteDialogContent,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Grid spacing={2} container>
      <Grid item xs={12}>
        <Typography variant="h5">{t(title)}</Typography>
        {subtitle && (
          <Typography
            variant="subtitle2"
            style={{ fontSize: '12px', marginTop: 10 }}>
            {t(subtitle)}
          </Typography>
        )}
      </Grid>
      {isEmpty(data) ? (
        <Grid item xs={12} style={{ marginTop: '8px' }}>
          <Typography style={{ color: '#696B6C' }}>
            {t(noFileDescription)}
          </Typography>
        </Grid>
      ) : (
        map(data, (item) => (
          <Grid item md={12} container key={item.id} alignItems="center">
            <Box
              fontSize="20px"
              marginRight="8px"
              component={IoDocumentTextOutline}
            />
            <Typography style={{ fontWeight: 700, flex: 1 }}>
              {item.caption || item.name}
            </Typography>
            <IconButton
              color="primary"
              aria-label="upload document"
              onClick={() => handleDownload(item.caption, item.id)}>
              <Box fontSize="20px" color="#546E7A" component={FiDownload} />
            </IconButton>
            <ConfirmDialog
              divider
              rejectText="cancel"
              confirmText="delete"
              title={deleteDialogTitle}
              dialogClassName={classes.dialog}
              btnClassName={classes.deleteButton}
              actionsClassName={classes.actions}
              content={<Typography>{deleteDialogContent}</Typography>}
              confirmAction={() => handleDelete(item.id)}>
              <IconButton color="primary" aria-label="Delete Document">
                <Box
                  fontSize="20px"
                  color="#E52A58"
                  component={MdDeleteForever}
                />
              </IconButton>
            </ConfirmDialog>
          </Grid>
        ))
      )}
      {(multiple ? true : isEmpty(data)) && (
        <Grid item xs={12}>
          <UploadDialog
            typeText={typeText}
            multiple={multiple}
            open={openUploadDialog}
            acceptFiles={acceptFiles}
            isUploading={isUploading}
            handleUpload={handleUpload}
            selectedFiles={selectedFiles}
            setOpen={setOpenUploadDialog}
            setSelectedFiles={setSelectedFiles}
            showAdd={multiple ? true : isEmpty(data)}
          />
          {!noDivider && <Divider style={{ marginTop: 24 }} />}
        </Grid>
      )}
    </Grid>
  );
};

Attachment.defaultProps = {
  data: [],
  multiple: true,
  title: 'attachment',
  noFileDescription: 'no_file_attached',
};

Attachment.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  multiple: PropTypes.bool,
  typeText: PropTypes.string,
  subtitle: PropTypes.string,
  isDeleting: PropTypes.bool,
  isUploading: PropTypes.bool,
  handleUpload: PropTypes.func,
  handleDelete: PropTypes.func,
  handleDownload: PropTypes.func,
  selectedFiles: PropTypes.array,
  setSelectedFiles: PropTypes.func,
  openUploadDialog: PropTypes.bool,
  noFileDescription: PropTypes.string,
  setOpenUploadDialog: PropTypes.func,
  acceptFiles: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
};

export default Attachment;
